/**
 * ClientRail — rail horizontal compacto pra Pinned/Recents (E2).
 *
 * Layout: linha de tiles compactos (sem expansion), navegavel horizontalmente.
 * Cada tile abre expansion no grid principal via onClick.
 *
 * Variant 'pinned' usa icone fixo PushPin; 'recents' usa Clock.
 */

import { Clock, PushPin } from '@phosphor-icons/react';

import { Eyebrow } from '@/components/ui/Eyebrow';
import { cn } from '@/lib/utils/cn';
import type { ClientSummary } from '@/lib/api/types';
import { ClientLogo } from './ClientLogo';

interface ClientRailProps {
  variant: 'pinned' | 'recents';
  clients: ClientSummary[];
  onSelect: (slug: string) => void;
  onUnpin?: (slug: string) => void;
}

const TITLE = {
  pinned: 'Fixados',
  recents: 'Acessados recentemente',
};

export function ClientRail({ variant, clients, onSelect, onUnpin }: ClientRailProps) {
  if (clients.length === 0) return null;

  const Icon = variant === 'pinned' ? PushPin : Clock;

  return (
    <section className="mb-5">
      <div className="mb-2 flex items-center gap-2">
        <Icon size={12} weight="fill" className="text-muted" aria-hidden="true" />
        <Eyebrow>{TITLE[variant]}</Eyebrow>
      </div>
      <div
        className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1.5"
        role="list"
        aria-label={TITLE[variant]}
      >
        {clients.map((c) => (
          <RailTile
            key={c.slug}
            client={c}
            variant={variant}
            onSelect={onSelect}
            onUnpin={onUnpin}
          />
        ))}
      </div>
    </section>
  );
}

interface RailTileProps {
  client: ClientSummary;
  variant: 'pinned' | 'recents';
  onSelect: (slug: string) => void;
  onUnpin?: (slug: string) => void;
}

function RailTile({ client, variant, onSelect, onUnpin }: RailTileProps) {
  return (
    <div
      role="listitem"
      className={cn(
        'group relative flex min-w-[180px] shrink-0 items-center gap-2.5 rounded-md border border-border bg-surface px-3 py-2',
        'transition-colors hover:border-ink/20 hover:bg-surface-muted/40',
      )}
    >
      <button
        type="button"
        onClick={() => onSelect(client.slug)}
        className="flex min-w-0 flex-1 items-center gap-2.5 text-left focus:outline-none"
        aria-label={`Abrir ${client.name}`}
      >
        <ClientLogo
          name={client.name}
          logoUrl={client.logo_url}
          sizeClassName="h-7 w-7 rounded"
          textClassName="text-[0.625rem]"
        />
        <span className="truncate text-[0.8125rem] font-medium text-ink">{client.name}</span>
      </button>
      {variant === 'pinned' && onUnpin && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onUnpin(client.slug);
          }}
          aria-label={`Desfixar ${client.name}`}
          className="shrink-0 rounded p-1 text-muted opacity-0 transition-opacity hover:bg-surface-muted hover:text-ink focus-visible:opacity-100 group-hover:opacity-100"
        >
          <PushPin size={11} weight="bold" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
